import {useSafeWebSocketPublicClient} from '#/hooks/use-safe-websocket-public-client';
import {useWeb3AuthSignatureGroup} from '#/hooks/use-web3auth-signature-group';
import {DefaultChildren} from '#/utils/utils';
import {CustomWeb3AuthConnector} from '#/wagmi/create-custom-web3auth-connector';
import {web3AuthInstance} from '#/wagmi/wagmi-config';
import {WALLET_ADAPTER_TYPE} from '@web3auth/base';
import {Web3AuthNoModal} from '@web3auth/no-modal';
import {OpenloginLoginParams} from '@web3auth/openlogin-adapter';
import {FunctionComponent, createContext} from 'react';
import {
  UseAccountReturnType,
  UsePublicClientReturnType,
  UseWalletClientReturnType,
  useAccount,
  useConfig,
  useConnect,
  useDisconnect,
  usePublicClient,
  useWalletClient,
} from 'wagmi';

export interface WalletContextValue {
  accountResult: UseAccountReturnType;
  publicClient: UsePublicClientReturnType;
  webSocketPublicClient: UsePublicClientReturnType | undefined;
  walletClient: UseWalletClientReturnType | undefined;
  connectWithWeb3Auth: (loginParams: {walletName: WALLET_ADAPTER_TYPE; loginParams?: OpenloginLoginParams}) => void;
  logout: () => void;
  web3Auth: Web3AuthNoModal | null;
  web3AuthSignatureResult: ReturnType<typeof useWeb3AuthSignatureGroup>;
}

const initialValue: WalletContextValue = {} as WalletContextValue;
export const WalletContext = createContext<WalletContextValue>(initialValue);

export const WalletProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const {chains, connectors} = useConfig();
  const web3AuthConnector = connectors[0] as CustomWeb3AuthConnector;

  const {connect} = useConnect();
  const {disconnect} = useDisconnect();

  const accountResult = useAccount();

  const publicClient = usePublicClient();
  const webSocketPublicClient = useSafeWebSocketPublicClient(publicClient, accountResult.chain);
  const walletClient = useWalletClient({
    account: accountResult.address,
  });

  const web3AuthSignatureResult = useWeb3AuthSignatureGroup(web3AuthInstance, accountResult);

  const connectWithWeb3Auth: WalletContextValue['connectWithWeb3Auth'] = loginParams => {
    web3AuthConnector.setAdapter(loginParams.walletName, loginParams.loginParams);
    connect({
      chainId: chains[0].id,
      connector: web3AuthConnector,
    });
  };

  return (
    <WalletContext.Provider
      value={{
        accountResult,
        publicClient,
        webSocketPublicClient,
        walletClient,
        connectWithWeb3Auth,
        logout: disconnect,
        web3Auth: web3AuthInstance,
        web3AuthSignatureResult,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
